import { Alert, Box, Button, CircularProgress, Step, StepLabel, Stepper, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../hooks/useAnalytics";
import { OnboardingStep } from "../../hooks/useOnboarding";
import { OnboardingComplete } from "./OnboardingComplete";
import { OnboardingLayout } from "./OnboardingLayout";
import { ServiceConnectionStep } from "./ServiceConnectionStep";
import { UserInfoStep } from "./UserInfoStep";
import { WorkingHoursStep } from "./WorkingHoursStep";

interface OnboardingFlowProps {
  onboardingState: {
    isOnboardingNeeded: boolean;
    isOnboardingComplete: boolean;
    currentStep: number;
    steps: OnboardingStep[];
    isLoading: boolean;
    error: string | null;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    markStepCompleted: (stepId: string) => void;
    refreshOnboardingStatus: () => Promise<void>;
    markOnboardingCompleted: () => Promise<void>;
  };
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onboardingState }) => {
  const navigate = useNavigate();
  const [hasNavigated, setHasNavigated] = React.useState(false);
  const { isOnboardingNeeded, isOnboardingComplete, currentStep, steps, isLoading, error, goToNextStep, goToPreviousStep, markStepCompleted, refreshOnboardingStatus, markOnboardingCompleted } =
    onboardingState;

  // Initialize analytics
  const analytics = useAnalytics();

  // Use useEffect to handle navigation - MUST be called before any early returns
  React.useEffect(() => {
    if (!isOnboardingNeeded && isOnboardingComplete && !hasNavigated) {
      setHasNavigated(true);
      // Track onboarding completion
      analytics.logOnboardingEvent("onboarding_complete", "completed", steps.length, steps.length);
      // Navigate to the home page when onboarding is complete
      navigate("/");
    }
  }, [isOnboardingNeeded, isOnboardingComplete, navigate, hasNavigated, analytics, steps.length]);

  if (isLoading) {
    return (
      <OnboardingLayout>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
          <CircularProgress size={32} sx={{ mr: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Checking setup status...
          </Typography>
        </Box>
      </OnboardingLayout>
    );
  }

  if (error) {
    // Track onboarding error
    analytics.logOnboardingEvent("onboarding_error", "failed", currentStep + 1, steps.length, "ONB_001");
    analytics.logError("ONB_001", `Onboarding error: ${error}`, {
      component: "OnboardingFlow",
      userAction: "onboarding_flow",
      severity: "high",
    });

    return (
      <OnboardingLayout>
        <Box textAlign="center">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Setup Error
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 1 }}>
              Try Again
            </Button>
          </Alert>
        </Box>
      </OnboardingLayout>
    );
  }

  if (!isOnboardingNeeded) {
    return null; // Don't show onboarding if not needed
  }

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const renderCurrentStep = () => {
    switch (currentStepData.id) {
      case "user_info":
        return (
          <UserInfoStep
            onComplete={(userInfo) => {
              console.log("User info collected:", userInfo);
              // Track user info step completion
              analytics.logOnboardingEvent("user_info", "completed", currentStep + 1, steps.length);
              markStepCompleted("user_info");
              goToNextStep();
            }}
          />
        );
      case "working_hours":
        return (
          <WorkingHoursStep
            onComplete={(workingHoursData) => {
              console.log("Working hours collected:", workingHoursData);
              // Track working hours step completion
              analytics.logOnboardingEvent("working_hours", "completed", currentStep + 1, steps.length);
              markStepCompleted("working_hours");
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );
      case "google_connection":
        return (
          <ServiceConnectionStep
            onComplete={() => {
              // Track Google connection step completion
              analytics.logOnboardingEvent("google_connection", "completed", currentStep + 1, steps.length);
              markStepCompleted("google_connection");
              goToNextStep();
            }}
            onRefresh={refreshOnboardingStatus}
          />
        );
      case "get_started":
        return <OnboardingComplete />;
      default:
        return (
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unknown Step</h3>
            <p className="text-gray-600">This onboarding step is not implemented yet.</p>
          </div>
        );
    }
  };

  return (
    <OnboardingLayout>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Welcome to Costaff.ai
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Box mb={4}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id} completed={step.completed}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: "0.875rem",
                      fontWeight: index <= currentStep ? 600 : 400,
                    },
                  }}
                >
                  {step.title}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Current step content */}
        <Box mb={4}>{renderCurrentStep()}</Box>

        {/* Navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button variant="outlined" onClick={goToPreviousStep} disabled={isFirstStep} sx={{ minWidth: 100 }}>
            Previous
          </Button>

          <Typography variant="body2" color="text.secondary">
            Step {currentStep + 1} of {steps.length}
          </Typography>

          {isLastStep && (
            <Button
              variant="contained"
              onClick={async () => {
                await markOnboardingCompleted();
                // Small delay to ensure state is updated before navigation
                setTimeout(() => {
                  navigate("/");
                }, 100);
              }}
              sx={{ minWidth: 100 }}
            >
              Get Started
            </Button>
          )}
        </Box>
      </Box>
    </OnboardingLayout>
  );
};
